import React, { useState, useMemo } from 'react';
import Button from '../Button';
import DocumentForm from '../DocumentForm';
import FontAwesomeIcon from '../Icon';
import Overlay from '../Overlay';
import Search from '../Search';
import Empty from '../Empty';
import Actions from '../Actions';
import { toast } from 'react-toastify';
import ContentBlock from '../ContentBlock';
import { IntroContent, USER_ROLES } from 'src/common/constants';
import { getUrlExtension, sortDocumentsList } from 'src/helpers/CommonHelpers';
import { DocumentFormData } from 'src/utils/S3UploadDocumentsUtility';
import download from 'downloadjs';
import ReusableLightbox from 'src/components/ReusableLightbox/reusable-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { groupDocuments } from 'src/components/Documents/DocumentGrouping';
import { DocumentGroup } from 'src/components/Documents/DocumentGroup';
import DocumentsRepeater from 'src/components/Documents/DocumentsRepeater';
import { filterDocumentsBySearchTerm } from 'src/components/DocumentsNewLocalState/helpers';
import '../Documents/Documents.scss'; // Todo: Importing the old SCSS for now eventually will be moved here.

interface DocumentsNewProps {
  documents: any[];
  breadcrumb?: any;
  onSaveDocument: (document: DocumentFormData) => void;
  onDeleteDocument: (documentId: string) => void;
  labelName: string;
  introContent?: boolean;
  disableGrouping?: boolean;
  from?: string;
  user: any;
}

const DocumentsNewWithState = ({
  documents,
  breadcrumb,
  onSaveDocument,
  onDeleteDocument,
  labelName,
  introContent,
  disableGrouping = false,
  from,
  user,
}: DocumentsNewProps) => {
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');

  const displayDocuments = useMemo(() => {
    const filtered = filterDocumentsBySearchTerm(documents, searchTerm);
    return sortDocumentsList(filtered);
  }, [documents, searchTerm]);

  const isSearchActive = searchTerm.length > 0;

  const handleSearch = (searchValue: any): void => {
    const value =
      typeof searchValue === 'string'
        ? searchValue
        : searchValue?.target?.value || '';
    setSearchTerm(value);
  };

  const onClick = (data: string) => {
    if (data.includes('.mp4')) {
      toast.success(
        `Your ${labelName} download has started, It may take some time.`,
        { autoClose: 12000, position: 'top-center' }
      );
    }
    download(data);
  };

  const onImageClicked = (x: number, url: string, isImage: boolean) => {
    if (isImage) {
      if (getUrlExtension(url) === 'heic')
        return window.open(url, '_blank')?.focus();
      setIsOpen(true);
      setSelectedImage(url);
      return;
    }
    window.open(url, '_blank')?.focus();
  };

  const homeOwnerCheck: boolean =
    user &&
    user.roles &&
    user.roles.length > 0 &&
    (user.roles[0].role.name === USER_ROLES.HOME_OWNER ||
      user.roles[0].role.name === USER_ROLES.OCCUPANT);

  const { priority, General, ...groupedDocuments } =
    groupDocuments(displayDocuments);

  const showAddButton = !(
    user.roles[0].role.name === USER_ROLES.STRATA_MANAGER ||
    user.roles[0].role.name === USER_ROLES.VENDOR ||
    user.roles[0].role.name === USER_ROLES.OCCUPANT ||
    user.roles[0].role.name === USER_ROLES.REALTOR ||
    (homeOwnerCheck && localStorage.getItem('theme') === 'Rize')
  );

  const handleSaveAndClose = (document: any) => {
    onSaveDocument(document);
    setShowOverlay(false);
  };

  return (
    <div className="documents">
      {introContent && (
        <ContentBlock content={IntroContent.manualsAndDocuments} />
      )}
      <Actions compact>
        <Search
          size="small"
          placeholder={`${labelName}...`}
          onChange={handleSearch}
          theme={undefined}
        />
        {showAddButton && (
          <Button
            color="teal"
            label={`Add ${labelName}`}
            onClick={() => {
              setShowOverlay(true);
            }}
            icon={
              <FontAwesomeIcon
                glyph="file-contract"
                weight="light"
                size="small"
              />
            }
            size="small"
          />
        )}
      </Actions>
      {displayDocuments.length > 0 ? (
        <>
          {isSearchActive || disableGrouping ? (
            <DocumentsRepeater
              documents={displayDocuments}
              onDeleteDocument={onDeleteDocument}
              onDownload={(data: any) => onClick(data)}
              onClick={(x: any, data: any, isImage): any =>
                onImageClicked(x, data, isImage)
              }
              user={user}
              from={from}
            />
          ) : (
            <>
              {/* Document Groups */}
              {priority.length > 0 && (
                <DocumentGroup
                  name="Priority"
                  documents={priority}
                  defaultExpanded
                  onDownload={(data) => onClick(data)}
                  onDeleteDocument={onDeleteDocument}
                  user={user}
                  onClick={(idx, url, isImage) =>
                    onImageClicked(idx, url, isImage)
                  }
                  from={undefined}
                />
              )}
              {Object.keys(groupedDocuments).map((group) => {
                if (groupedDocuments[group].length === 0) return null;
                return (
                  <DocumentGroup
                    key={group}
                    name={group}
                    documents={groupedDocuments[group]}
                    onDownload={(data: any) => onClick(data)}
                    onDeleteDocument={onDeleteDocument}
                    user={user}
                    onClick={(idx: any, url: any, isImage: any) =>
                      onImageClicked(idx, url, isImage)
                    }
                    from={undefined}
                  />
                );
              })}
              {General.length > 0 && (
                <DocumentGroup
                  name="Manuals & Documents "
                  documents={General}
                  onDownload={(data: any) => onClick(data)}
                  onDeleteDocument={onDeleteDocument}
                  user={user}
                  onClick={(idx: any, url: any, isImage: any) =>
                    onImageClicked(idx, url, isImage)
                  }
                  from={undefined}
                />
              )}
            </>
          )}
          {isOpen && (
            <ReusableLightbox
              isOpen={isOpen}
              onClose={() => setIsOpen(false)}
              image={selectedImage}
            />
          )}
        </>
      ) : (
        <Empty
          headline={`No ${labelName} yet`}
          strapline={`All ${labelName} you add will be listed here so it's easier for you to manage.`}
          searchIsActive={searchTerm}
          cta={
            showAddButton ? (
              <div>
                <Button
                  color="teal"
                  label={`Add ${labelName}`}
                  onClick={() => {
                    setShowOverlay(true);
                  }}
                  icon={
                    <FontAwesomeIcon
                      glyph="file-contract"
                      weight="light"
                      size="small"
                    />
                  }
                  size="small"
                />
              </div>
            ) : null
          }
          image={
            <img
              alt={`No ${labelName} yet`}
              src="/assets/empty/documents.svg"
            />
          }
          direction={undefined}
        />
      )}
      <Overlay
        showOverlay={showOverlay}
        content={
          <DocumentForm
            onSaveDocument={handleSaveAndClose}
            from={from}
            breadcrumb={breadcrumb}
            user={user}
            onClose={() => {
              setShowOverlay(false);
            }}
          />
        }
      />
    </div>
  );
};

export default DocumentsNewWithState;
